import { TextField, FormGroup, FormControlLabel, Switch, Tooltip, Select, MenuItem, FormControl, InputLabel, Alert, Paper, Box } from "@mui/material";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from "dayjs";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from "@mui/x-date-pickers";
import Stack from "@mui/material/Stack";
import { Typography } from "@mui/material";
import {useRef, useEffect, useState} from 'react';

/**
 * 
 * @param {props} selectedFinancial
 * @param {props} setSelectedFinancial
 * @returns 
 */
function FormPrep(props) {
    const [errors, setErrors] = useState({});
    
    const divisions = [
        "Electrical",
        "Low Voltage",
        "HVAC/Sheet Metal/Mechanical",
        "Architectural Metal and Glass",
        "Civil",
        "General Contractor",
        "Glass and Glazing",
        "Paving and Grading",
        "Plumbing",
        "Mechanical"
    ];

    const validateForm = () => {
        const newErrors = {};
        
        // Validate division
        if (!props.selectedFinancial.division) {
            newErrors.division = 'Division is required';
        }
        
        // Validate peer group ID
        if (!props.selectedFinancial.peerGroupId || props.selectedFinancial.peerGroupId === 0) {
            newErrors.division = 'Please select a valid division';
        }
        
        // Validate fiscal start date
        if (!props.selectedFinancial.fiscalStart) {
            newErrors.fiscalStart = 'Fiscal start date is required';
        }
        
        // Validate fiscal end date
        if (!props.selectedFinancial.fiscalEnd) {
            newErrors.fiscalEnd = 'Fiscal end date is required';
        }
        
        // Validate date logic
        if (props.selectedFinancial.fiscalStart && props.selectedFinancial.fiscalEnd) {
            const startDate = new Date(props.selectedFinancial.fiscalStart);
            const endDate = new Date(props.selectedFinancial.fiscalEnd);
            
            if (startDate >= endDate) {
                newErrors.fiscalEnd = 'Fiscal end date must be after fiscal start date';
            }
            
            // Check if fiscal year is reasonable (between 300 and 400 days)
            const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
            if (daysDiff < 300 || daysDiff > 400) {
                newErrors.fiscalEnd = 'Fiscal year should be approximately 365 days';
            }
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const onChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'division') {
            // When division changes, also set the peerGroupId
            const selectedPeerGroup = props.peerGroups.find(pg => pg.division === value);
            console.log('FormPrep: Division selected:', value);
            console.log('FormPrep: Available peer groups:', props.peerGroups);
            console.log('FormPrep: Selected peer group:', selectedPeerGroup);
            
            props.setSelectedFinancial({ 
                ...props.selectedFinancial, 
                [name]: value,
                peerGroupId: selectedPeerGroup ? selectedPeerGroup.peerGroupId : 0
            });
        } else {
            props.setSelectedFinancial({ ...props.selectedFinancial, [name]: value });
        }
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    }

    const onChecked = (e) => {
        const { name, checked } = e.target;
        props.setSelectedFinancial({ ...props.selectedFinancial, [name]: checked });
    }
  // Create ref for first input
  const firstInputRef = useRef(null);

  // Focus on first input when component mounts
  useEffect(() => {
      if (firstInputRef.current) {
          firstInputRef.current.focus();
      }
      
  }, []); // Empty dependency array means this runs once when component mounts

    const onDateChange = (name, newValue) => {
        const updatedFinancial = { 
            ...props.selectedFinancial, 
            [name]: newValue ? newValue.toISOString() : null 
        };
        
        // If this is a fiscal start date change
        if (name === 'fiscalStart' && newValue && !props.selectedFinancial.isYTD) {
            
            // not YTD, auto-calculate fiscal end
            const fiscalStartDate = new Date(newValue);
            const fiscalEndDate = new Date(fiscalStartDate);
            fiscalEndDate.setDate(fiscalStartDate.getDate() + 364); // 364 days = 365 total days for fiscal year
            updatedFinancial.fiscalEnd = fiscalEndDate.toISOString();
        }
        
        
        props.setSelectedFinancial(updatedFinancial);
        
        // Clear error when user selects a date
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Expose validation function to parent component
    useEffect(() => {
        if (props.onValidationChange) {
            props.onValidationChange(validateForm);
        }
    }, [props.selectedFinancial, errors]);

    return (<>
        {/* Fiscal Type Selection - Now at the top */}
        <div className="formRow">
            <Paper elevation={2} sx={{ p: 3, mb: 3, backgroundColor: '#f8f9fa' }}>
                <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', mb: 2, fontWeight: 'bold' }}>
                    Select Report Type
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Typography variant="body1" sx={{ fontWeight: props.selectedFinancial.isYTD ? 'normal' : 'bold', color: props.selectedFinancial.isYTD ? 'text.secondary' : 'primary.main' }}>
                            Full Fiscal Year
                        </Typography>
                        <Switch 
                            checked={props.selectedFinancial.isYTD} 
                            onChange={onChecked} 
                            name='isYTD'
                            size="medium"
                        />
                        <Typography variant="body1" sx={{ fontWeight: props.selectedFinancial.isYTD ? 'bold' : 'normal', color: props.selectedFinancial.isYTD ? 'primary.main' : 'text.secondary' }}>
                            Year-to-Date (YTD)
                        </Typography>
                    </Stack>
                </Box>
                <Typography variant="body2" sx={{ textAlign: 'center', mt: 1, color: 'text.secondary' }}>
                    {props.selectedFinancial.isYTD 
                        ? "Report data from the beginning of the fiscal year up to a specific date"
                        : "Report data for the complete fiscal year"
                    }
                </Typography>
            </Paper>
        </div>

        {/* I want to use name and value destructuring to get the property I want to tweak on the object - this passes the fully formed object back out of this component to the financial stepper */}
        <div className="formRow">
            <div className="formGroup">
                <Tooltip title="The division for which the company is reporting the data. If the company does not have multiple divisions, select the trade the company is most active." arrow placement="top">
                    <FormControl className="formControl" fullWidth error={!!errors.division}>
                        <InputLabel id="division-label">Division Name</InputLabel>
                        <Select
                            labelId="division-label"
                            id="division-select"
                            value={props.selectedFinancial.division || ''}
                            label="Division Name"
                            onChange={onChange}
                            className="formControl"
                            name='division'
                            inputRef={firstInputRef}  // Add the ref here for initial focus
                        >
                            {
                                props.peerGroups.map((peerGroup) => (
                                    <MenuItem key={peerGroup.peerGroupId} value={peerGroup.division}>
                                        {peerGroup.division}
                                    </MenuItem>
                                ))
                            }
                        </Select>
                        {errors.division && (
                            <Alert severity="error" sx={{ mt: 1, fontSize: '0.75rem' }}>
                                {errors.division}
                            </Alert>
                        )}
                    </FormControl>
                </Tooltip>
            </div>
            
        {/* </div>
        <div className="formRow"> */}
        <LocalizationProvider dateAdapter={AdapterDayjs}>
                {/* <div className="formGroup">
                    <DatePicker className="formControl" label="Report Date" variant="standard" value={props.selectedFinancial !== undefined ? dayjs(props.selectedFinancial.reportDate) : dayjs(new Date())} name='reportDate' />
                </div> */}
                <div className="formGroup">
                    <Tooltip 
                        title={props.selectedFinancial.isYTD ? "Enter the last day of the period for which you are reporting including the year" : "Enter the first day of your fiscal year for which you are reporting including the year"} 
                        arrow 
                        placement="top"
                    >
                        <DatePicker 
                            className="formControl" 
                            label={props.selectedFinancial.isYTD ? "Reporting Up To" : "Fiscal Start"} 
                            variant="standard" 
                            value={props.selectedFinancial.fiscalStart ? dayjs(props.selectedFinancial.fiscalStart) : null}
                            onChange={(newValue) => 
                                
                                onDateChange('fiscalStart', newValue)}
                            name='fiscalStart'
                            slotProps={{
                                textField: {
                                    error: !!errors.fiscalStart,
                                    helperText: errors.fiscalStart || ''
                                }
                            }}
                        />
                    </Tooltip>
                </div>
                <div className="formGroup">
                    <Tooltip title="Enter the last day of the company's annual fiscal year for which you are reporting including the year." arrow placement="top">
                        <DatePicker 
                            className="formControl" 
                            label={props.selectedFinancial.isYTD? "For Fiscal Year Ending":"Fiscal End" }
                            variant="standard" 
                            value={props.selectedFinancial.fiscalEnd ? dayjs(props.selectedFinancial.fiscalEnd) : null}
                            onChange={(newValue) => onDateChange('fiscalEnd', newValue)}
                            name='fiscalEnd'
                            slotProps={{
                                textField: {
                                    error: !!errors.fiscalEnd,
                                    helperText: errors.fiscalEnd || ''
                                }
                            }}
                        />
                    </Tooltip>
                </div>            
        </LocalizationProvider>
        </div>
        
        {/* Display general form errors */}
        {Object.keys(errors).length > 0 && (
            <div className="formRow">
                <Alert severity="error" sx={{ width: '100%' }}>
                    Please fix the errors above before submitting your fiscal report.
                </Alert>
            </div>
        )}
    </>)
}

export default FormPrep;